import { useBreakpoint } from '@/hooks/useBreakpoint';
import { ChevronDown } from '@/icons';
import type { ImageObject } from '@/types/global';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

interface PromotionAccordionProps {
  title: string;
  image: ImageObject;
  imageMobile?: ImageObject;
  appStoreLink?: string;
  googleStoreLink?: string;
  items: {
    title: string;
    content: string;
  }[];
}

const PromotionAccordion: React.FC<PromotionAccordionProps> = (props) => {
  const [openId, setOpenId] = useState(0);
  const { image, imageMobile, items, googleStoreLink, appStoreLink } = props;
  const { isMobile } = useBreakpoint();

  return (
    <div className="promotion-accordion">
      <div className="element--pad">
        <div className="container">
          <div className="promotion-accordion__inner">
            <div className="promotion-accordion__image">
              {isMobile && imageMobile ? (
                <img
                  src={imageMobile.url}
                  alt={props.title}
                  loading="lazy"
                  width={imageMobile.width}
                  height={imageMobile.height}
                />
              ) : (
                <img
                  src={image.url}
                  alt={props.title}
                  loading="lazy"
                  width={image.width}
                  height={image.height}
                />
              )}
            </div>

            <div className="promotion-accordion__content">
              <div className="promotion-accordion__header">
                <h2 className="h3">{props.title}</h2>

                {items.map((item, index) => {
                  const isOpen = index === openId;

                  return (
                    <div
                      key={index}
                      className={classNames('accordion__item', {
                        'accordion__item--open': isOpen,
                      })}
                    >
                      <button
                        className="accordion__item-heading"
                        onClick={() => setOpenId(index)}
                        title={
                          isOpen ? `Close ${item.title}` : `Open ${item.title}`
                        }
                      >
                        <div className="accordion__title">{item.title}</div>
                        <ChevronDown className="accordion__item-chevron" />
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            key="content"
                            initial="collapsed"
                            animate="open"
                            exit="collapsed"
                            variants={{
                              open: { opacity: 1, height: 'auto' },
                              collapsed: { opacity: 0, height: 0 },
                            }}
                            transition={{
                              duration: 0.3,
                              ease: [0.04, 0.62, 0.23, 0.98],
                            }}
                          >
                            <div
                              className="accordion__content"
                              dangerouslySetInnerHTML={{ __html: item.content }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}

                {appStoreLink || googleStoreLink ? (
                  <div className="promotion-accordion__cta">
                    {appStoreLink && (
                      <a
                        href={appStoreLink}
                        target="_blank"
                        data-app-link="iOS"
                        rel="noreferrer"
                      >
                        <img
                          className="rewards-intro__app-image"
                          src="/_resources/app/client/img/app-store.svg"
                          alt="Download on the App Store"
                        />
                      </a>
                    )}
                    {googleStoreLink && (
                      <a
                        href={googleStoreLink}
                        target="_blank"
                        data-app-link="Android"
                        rel="noreferrer"
                      >
                        <img
                          className="rewards-intro__app-image"
                          src="/_resources/app/client/img/google-play.svg"
                          alt="Get it on Google Play"
                        />
                      </a>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromotionAccordion;
